import React, {useEffect, useMemo, useState} from "react";
import {Button, Chip, CircularProgress, Drawer, Typography} from "@mui/material";
import {CloseOutlined, OpenInNewOutlined, WarningAmberOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router";

import Attachments from "../Common/Attachments";
import FormRenderer from "../Forms/FormRenderer";
import {formatLocal} from "../../util/Formatters";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {classificationByCode} from "../../util/objectClassifications";
import {Blue3, Grey2, Grey5, Grey6, Grey17, White} from "../../theme/styles";

// The record, under the queue, while you decide whether to sign it.
//
// WHY IT EXISTS. The queue's own subtitle says "what you sign is the record as it was submitted".
// Honouring that used to mean leaving the queue, reading the record, and finding your way back --
// so the cheapest path through the screen was to sign without reading, which is the one outcome a
// signature is supposed to rule out. Putting the record in front of the person attesting to it is
// the point.
//
// READ-ONLY, AND NOT A LITTLE BIT EDITABLE. Every control here is rendered with readOnly and there
// is no Save anywhere: a drawer somebody can type into invites editing mid-approval, and editing a
// record after it was submitted is exactly what invalidates the signatures already on it.
//
// IT REUSES THE DETAIL TAB'S RENDERERS rather than reimplementing them. A second implementation of
// "how a form value looks" would drift from the first, and the moment it did, the thing somebody
// signed would not be the thing the record shows.

type Props = {
    open: boolean;
    onClose: () => void;
    /** The approval request being considered. */
    request: Record<string, any> | null;
};

export default function RecordReviewDrawer({open, onClose, request}: Props) {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    // MEMOISED, AND THE EFFECT BELOW DEPENDS ON IT. Built fresh each render it is a new object
    // every time, so the effect's dependency array never matches, the effect re-runs, its
    // setLoading triggers another render, and the drawer fetches for ever behind a spinner
    // that cannot stop. Every other component here memoises it for this reason.
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    const [record, setRecord] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const recordUSID = request?.RecordUSID ?? null;

    useEffect(() => {
        if (!open || !recordUSID) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        api.get("t1/v1/object_revisions", {params: {USID: recordUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
                const rows = Array.isArray(list) ? list : [list];
                setRecord(rows[0] ?? null);
            })
            .catch((problem: any) => {
                if (cancelled) return;
                // THE REASON, NOT A SHRUG. A reviewer who cannot open the record needs to know
                // whether that is a permission problem or a missing record, because the two have
                // different people to ask.
                setError(problem?.response?.data?.Cause ?? "That record could not be opened.");
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
        // `api` is memoised on sessionContext above, which is what makes this array stable.
    }, [open, recordUSID, api]);

    if (!request) return null;

    const classification = classificationByCode(record?.ObjectClassification);

    // EDITED SINCE IT WAS SUBMITTED, worked out from dates rather than by re-hashing the record.
    // The server settles this properly when somebody signs -- it compares a content hash and
    // answers CC-OAPR-STAL -- but that costs a query per request and the queue reads many. Comparing
    // timestamps is cheap and errs the safe way: it can warn when only an incidental field moved,
    // which is a wasted glance, where staying silent about a real edit would be a bad signature.
    const submitted = request.RequestedDatetime ? new Date(request.RequestedDatetime).getTime() : null;
    const touched = record?.LastModifiedDatetime ? new Date(record.LastModifiedDatetime).getTime() : null;
    const editedSince = submitted !== null && touched !== null && touched > submitted + 1000;

    return (
        <Drawer anchor="bottom" open={open} onClose={onClose}
                slotProps={{paper: {sx: {height: "72vh", borderTopLeftRadius: 12,
                                         borderTopRightRadius: 12}}}}>
            <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
                {/* ── what you are looking at ─────────────────────────────────────────────── */}
                <div style={{display: "flex", alignItems: "center", gap: 12, padding: "14px 20px",
                             borderBottom: `1px solid ${Grey2}`, backgroundColor: White}}>
                    {request.RecordObjectID ? (
                        <Typography style={{fontSize: 15, fontWeight: 700, color: Blue3}}>
                            {request.RecordObjectID}
                        </Typography>
                    ) : null}
                    <Typography style={{fontSize: 15, fontWeight: 500, color: Grey17}}>
                        {request.RecordName}
                    </Typography>
                    {/* WHICH VERSION. Ambiguity about what is on screen is worse than no drawer:
                        a signature is given against a specific revision. */}
                    <Chip size="small" label={`rev ${request.RecordRevision ?? "—"}`}
                          sx={{fontSize: 11, height: 22}}/>
                    <Typography style={{fontSize: 12, color: Grey6}}>
                        as submitted {formatLocal(request.RequestedDatetime)}
                    </Typography>

                    <div style={{flex: 1}}/>

                    {record && classification ? (
                        <Button size="small" startIcon={<OpenInNewOutlined sx={{fontSize: 16}}/>}
                                onClick={() => navigate(`${classification.route}/${recordUSID}`)}
                                sx={{textTransform: "none"}}>
                            Open the full record
                        </Button>
                    ) : null}
                    <Button size="small" startIcon={<CloseOutlined sx={{fontSize: 16}}/>}
                            onClick={onClose} sx={{textTransform: "none"}}>
                        Close
                    </Button>
                </div>

                {editedSince ? (
                    <div style={{display: "flex", alignItems: "center", gap: 8, padding: "10px 20px",
                                 backgroundColor: "#FFFAEB", borderBottom: "1px solid #FEDF89"}}>
                        <WarningAmberOutlined sx={{fontSize: 18, color: "#B54708"}}/>
                        <Typography style={{fontSize: 13, color: "#8A5A00"}}>
                            This record has been edited since it was submitted
                            ({formatLocal(record?.LastModifiedDatetime)}). Signatures cover the record as
                            it was submitted, so signing may be refused until it is submitted again.
                        </Typography>
                    </div>
                ) : null}

                {/* ── the record ──────────────────────────────────────────────────────────── */}
                <div style={{flex: 1, overflow: "auto", padding: "18px 20px", backgroundColor: "#FCFCFD"}}>
                    {loading ? (
                        <CircularProgress size={22}/>
                    ) : error ? (
                        <Typography style={{fontSize: 13, color: "#B42318"}}>{error}</Typography>
                    ) : !record ? (
                        <Typography style={{fontSize: 13, color: Grey5}}>Nothing to show.</Typography>
                    ) : (
                        <>
                            <Field label="Name" value={record.Name}/>
                            <Field label="Description" value={record.Description}/>
                            <div style={{display: "flex", gap: 32, flexWrap: "wrap", margin: "10px 0 18px"}}>
                                <Field inline label="Status" value={record.Status}/>
                                <Field inline label="Revision" value={record.DisplayRevision}/>
                                <Field inline label="Owner" value={record.PrimaryPositionName}/>
                                <Field inline label="Effective" value={formatLocal(record.EffectiveDatetime)}/>
                            </div>

                            {/* THE FORM, IF IT HAS ONE. For most controlled documents the substance
                                is here rather than in the fields above. */}
                            {record.SeededFromRevisionUSID ? (
                                <div style={{marginBottom: 20}}>
                                    <Heading>Form</Heading>
                                    <FormRenderer compositeRevisionUSID={record.SeededFromRevisionUSID}
                                                  objectRevisionUSID={recordUSID}
                                                  value={record.FormInput}
                                                  onChange={() => undefined}
                                                  readOnly/>
                                </div>
                            ) : null}

                            {/* THE FILES, WHICH FOR AN SOP ARE USUALLY THE ACTUAL CONTENT. A drawer
                                showing only fields would give false confidence that you had read
                                the thing you are signing.

                                allFiles, AND THIS IS THE SURFACE WHERE ITS ABSENCE COST MOST. The
                                panel listed only attachments with no field code, so a reviewer
                                about to sign a record whose four certificates all answer File
                                fields was shown "No files." -- false confidence of exactly the kind
                                the comment above was written to prevent, under a heading promising
                                the opposite. A signature is the one place a subset must not wear
                                the name of the whole. */}
                            <Heading>Attachments</Heading>
                            <Attachments sourceUSID={String(recordUSID)} allFiles readOnly/>
                        </>
                    )}
                </div>
            </div>
        </Drawer>
    );
}

function Heading({children}: {children: React.ReactNode}) {
    return (
        <Typography style={{fontSize: 12, fontWeight: 700, color: Grey6, letterSpacing: "0.06em",
                            textTransform: "uppercase", margin: "6px 0 8px"}}>
            {children}
        </Typography>
    );
}

function Field({label, value, inline}: {label: string; value: any; inline?: boolean}) {
    return (
        <div style={{marginBottom: inline ? 0 : 12}}>
            <Typography style={{fontSize: 11, color: Grey6, textTransform: "uppercase",
                                letterSpacing: "0.05em"}}>
                {label}
            </Typography>
            <Typography style={{fontSize: 14, color: Grey17, whiteSpace: "pre-wrap"}}>
                {value === null || value === undefined || value === "" ? "—" : String(value)}
            </Typography>
        </div>
    );
}
